import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getCategories, getResource, getResourceDetails } from '@api/resource';
import { ICategory, IResource, IResourceDetails } from '@interfaces/resources';
import { RootState } from '@store/store.interface';

interface ResourcesState {
  categories: ICategory[];
  selectedCategoryId: number | null;
  subCategoryId: number | null;
  resources: IResource[];
  resourcesError: string | null;
  resourcesIsLoading: boolean;
  resourceDetails: IResourceDetails | null;
  resourceError: string | null;
  resourceIsLoading: boolean;
}

export const fetchCategories = createAsyncThunk('fetchCategories', async () => {
  const response = await getCategories();
  return response.data;
});
export const fetchResources = createAsyncThunk('fetchResource', async (id: number) => {
  const response = await getResource(id);
  return response.data;
});
export const fetchResourceDetails = createAsyncThunk('fetchResourceDetails', async (id: number) => {
  const response = await getResourceDetails(id);
  return response.data;
});

const initialState: ResourcesState = {
  categories: [],
  subCategoryId: null,
  selectedCategoryId: null,
  resources: [],
  resourcesIsLoading: false,
  resourcesError: null,
  resourceDetails: null,
  resourceIsLoading: false,
  resourceError: null,
};

const resourcesSlice = createSlice({
  name: 'resources',
  initialState,
  reducers: {
    setSelectedCategoryId: (state, action) => {
      state.selectedCategoryId = action.payload;
    },
    setSubcategoryId: (state, action) => {
      state.subCategoryId = action.payload;
    },
    clearError: state => {
      state.resourcesError = null;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchCategories.fulfilled, (state, action) => {
      state.categories = action.payload;
    });
    builder.addCase(fetchResources.fulfilled, (state, action) => {
      state.resourcesIsLoading = false;
      state.resources = action.payload;
    });
    builder.addCase(fetchResources.pending, state => {
      state.resourcesIsLoading = true;
      state.resourcesError = null;
    });
    builder.addCase(fetchResources.rejected, (state, action) => {
      state.resourcesIsLoading = false;
      state.resourcesError = action.error.message || 'An error occurred';
    });
    builder.addCase(fetchResourceDetails.fulfilled, (state, action) => {
      state.resourceIsLoading = false;
      state.resourceDetails = action.payload;
    });
    builder.addCase(fetchResourceDetails.pending, state => {
      state.resourceIsLoading = true;
      state.resourceError = null;
    });
    builder.addCase(fetchResourceDetails.rejected, (state, action) => {
      state.resourceIsLoading = false;
      state.resourceError = action.error.message || 'An error occurred';
    });
  },
});

export default resourcesSlice.reducer;

export const { setSelectedCategoryId, setSubcategoryId } = resourcesSlice.actions;

export const selectCategories = (state: RootState) => state.resources.categories;
export const selectCategoryId = (state: RootState) => state.resources.selectedCategoryId;

export const selectResources = (state: RootState) => state.resources.resources;
export const selectResourcesIsLoading = (state: RootState) => state.resources.resourcesIsLoading;
export const selectResourcesError = (state: RootState) => state.resources.resourcesError;

export const selectResourceDetails = (state: RootState) => state.resources.resourceDetails;
export const selectResourceIsLoading = (state: RootState) => state.resources.resourceIsLoading;
export const selectResourceError = (state: RootState) => state.resources.resourceError;
